// slothfulchat-web: pure extractor for the chatmail relay-directory JSON.
// Kept free of browser/runtime imports so the unit tests
// (src/tests/relayDirectory.test.ts) can load it under plain node — the
// fetch/DNS-probe glue lives in relayDirectory.ts.
//
// Expected shape (produced by the chatmail-relays-mirror repo, a dumb
// automated mirror of https://chatmail.at/relays):
//   { "source": "…", "fetchedAt": "…", "relays": [ { "host": "…" }, … ] }
// The extractor is defensive anyway: the document is fetched from the network
// at runtime, so anything malformed — wrong root type, junk entries, invalid
// hostnames — is skipped rather than thrown, and callers get whatever valid
// hosts remain (possibly none). Unknown fields are ignored.

// something that plausibly is a public DNS name: dotted labels, letters-only
// TLD, no scheme/path leftovers
const HOSTNAME_RE =
  /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/

/**
 * Normalize a user-typed relay host (trim, lowercase) and return it only when
 * it looks like a bare public hostname, else `undefined`. Shares the exact
 * shape the directory extractor accepts, so a hand-typed relay in the picker's
 * "Other…" field is validated the same way as a fetched one.
 */
export function normalizeRelayHost(value: string): string | undefined {
  const h = value.trim().toLowerCase()
  return HOSTNAME_RE.test(h) ? h : undefined
}

/**
 * Relay hostnames from a parsed relay-directory JSON document: lowercased,
 * validated against a strict hostname shape, deduplicated, document order
 * preserved. Malformed documents or entries yield `[]` / get skipped.
 */
export function parseRelayDirectory(data: unknown): string[] {
  const relays = (data as { relays?: unknown } | null | undefined)?.relays
  if (!Array.isArray(relays)) {
    return []
  }
  const found: string[] = []
  const seen = new Set<string>()
  for (const entry of relays) {
    const host = (entry as { host?: unknown } | null | undefined)?.host
    if (typeof host !== 'string') continue
    const h = host.trim().toLowerCase()
    if (!HOSTNAME_RE.test(h) || seen.has(h)) continue
    seen.add(h)
    found.push(h)
  }
  return found
}
